import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { ReactComponent as BasketIcon } from './basket.svg';
import basketImage from './basket-gold-icon.jpg';


export default function Navbar() {
    const [menuOpen, setMenuOpen] = useState(false);

    const [demoOpen, setDemoOpen] = useState(false);
    const demoRef = useRef(null); // Ref for Emura Tech dropdown

    const [editionOpen, setEditionOpen] = useState(false);
    const editionRef = useRef(null); // Ref for Emura Tech dropdown

    const otherNavItems = ["Emura Community", "Trainings", "All Pages"];

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (demoRef.current && !demoRef.current.contains(event.target)) {
                setDemoOpen(false);
            }
            if (editionRef.current && !editionRef.current.contains(event.target)) {
                setEditionOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <nav className="flex justify-between items-center px-6 py-4 bg-black text-white relative z-50">
            <Link to="/" className="text-2xl font-bold">
                {/*Emura Group<sup>®</sup>*/}
                <img
                    src="core-images/logo.png"
                    className="logo"
                    alt="logo"
                    onContextMenu={(e) => e.preventDefault()} // prevent right-click
                    draggable="false" // prevent drag
                    style={{ userSelect: 'none', pointerEvents: 'auto' }}
                />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-4 items-center">
                {/* Emura Tech dropdown */}
                <div className="relative group" ref={demoRef}>
                    <button onClick={() => setDemoOpen(!demoOpen)} className="px-3 py-2 bg-gray-800 rounded hover:bg-gray-700">
                        Emura Tech ⌄
                    </button>
                    <AnimatePresence>
                        {demoOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute bg-white text-black mt-2 rounded shadow-lg p-2 space-y-2 z-50"
                            >
                                <Link to="/our-vision" className="block px-4 py-2 hover:bg-gray-100 rounded">Our Vision</Link>
                                <Link to="/our-goal" className="block px-4 py-2 hover:bg-gray-100 rounded">Our Goal</Link>
                                <Link to="/our-services" className="block px-4 py-2 hover:bg-gray-100 rounded">Our Services</Link>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Emura Edition dropdown */}
                <div className="relative group" ref={editionRef}>
                    <button onClick={() => setEditionOpen(!editionOpen)} className="px-3 py-2 bg-gray-800 rounded hover:bg-gray-700">
                        Emura Edition ⌄
                    </button>
                    <AnimatePresence>
                        {editionOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute bg-white text-black mt-2 rounded shadow-lg p-2 space-y-2 z-50"
                            >
                                <Link to="/our-mission" className="block px-4 py-2 hover:bg-gray-100 rounded">Our Mission</Link>
                                <Link to="/editorial-line" className="block px-4 py-2 hover:bg-gray-100 rounded">Editorial Line</Link>
                                <Link to="/our-library" className="block px-4 py-2 hover:bg-gray-100 rounded">Our Library</Link>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Other nav items */}
                {otherNavItems.map((item) => (
                    <Link
                        key={item}
                        to={`/${item.toLowerCase().replace(/ /g, "-")}`}
                        className="px-3 py-2 bg-gray-800 rounded hover:bg-gray-700"
                    >
                        {item} ⌄
                    </Link>
                ))}
            </div>

            {/* Desktop Contact/Cart */}
            <div className="hidden md:flex items-center gap-4">
                {/*<span className="gold-basket">🛒</span>*/}
                {/*<BasketIcon className="w-6 h-6 text-gold" />*/}
                <img src={basketImage} alt="Panier" className="w-12 h-12 text-yellow-500" />
                <Link to="/contact" className="contact text-black px-4 py-2 rounded-full">
                    Contact us
                </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <div className="md:hidden flex items-center">
                <button onClick={() => setMenuOpen(!menuOpen)}>
                    {menuOpen ? <X /> : <Menu />}
                </button>
            </div>

            {/* Mobile Dropdown Menu */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 w-full bg-gray-900 p-4 flex flex-col space-y-2 md:hidden z-50"
                    >
                        {/* Emura Tech Group */}
                        <div className="text-white font-semibold">Emura Tech</div>
                        <Link to="/our-vision" onClick={() => setMenuOpen(false)} className="pl-4 text-left px-4 py-2 rounded hover:bg-gray-700">Our Vision</Link>
                        <Link to="/our-services" onClick={() => setMenuOpen(false)} className="pl-4 text-left px-4 py-2 rounded hover:bg-gray-700">Our Services</Link>

                        {/* Emura Edition Group */}
                        <div className="text-white font-semibold mt-4">Emura Edition</div>
                        <Link to="/our-mission" onClick={() => setMenuOpen(false)} className="pl-4 text-left px-4 py-2 rounded hover:bg-gray-700">Our Mission</Link>
                        <Link to="/editorial-line" onClick={() => setMenuOpen(false)} className="pl-4 text-left px-4 py-2 rounded hover:bg-gray-700">Editorial Line</Link>
                        <Link to="/our-library" onClick={() => setMenuOpen(false)} className="pl-4 text-left px-4 py-2 rounded hover:bg-gray-700">Our Library</Link>

                        {/* Other Nav Items */}
                        {otherNavItems.map((item) => (
                            <Link
                                key={item}
                                to={`/${item.toLowerCase().replace(/ /g, "-")}`}
                                onClick={() => setMenuOpen(false)}
                                className="text-left px-4 py-2 rounded hover:bg-gray-700"
                            >
                                {item}
                            </Link>
                        ))}

                        {/* Contact */}
                        <Link
                            to="/contact"
                            onClick={() => setMenuOpen(false)}
                            className="bg-white text-black px-4 py-2 rounded-full w-fit mx-auto mt-4"
                        >
                            Contact us
                        </Link>
                    </motion.div>
                )}
            </AnimatePresence>

        </nav>
    );
}
